import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Compact newsletter signup widget.
 * Used in the public footer and (optionally) anywhere else we want to capture
 * non-member email addresses for the monthly newsletter distribution list.
 */
const NewsletterSignup = ({ source = 'footer', compact = false }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim(),
        source,
      });
      setDone(true);
      setEmail('');
      toast.success("You're on the list — talk soon!");
    } catch {
      toast.error("Couldn't subscribe — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <p className={`text-sm ${compact ? 'text-cream/80' : 'text-maroon/70'}`} data-testid="newsletter-signup-success">
        Thanks for subscribing — see you in your inbox!
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-2" data-testid="newsletter-signup-form">
      <div className="relative">
        <Mail size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${compact ? 'text-cream/50' : 'text-maroon/40'}`} />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          required
          className={`w-full pl-9 pr-3 py-2 rounded-sm border-2 focus:outline-none ${compact ? 'bg-cream/10 border-cream/20 text-cream placeholder:text-cream/40 focus:border-cream/60' : 'bg-white border-maroon/20 text-maroon focus:border-maroon'}`}
          data-testid="newsletter-signup-email"
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        className={`w-full inline-flex items-center justify-center gap-2 py-2 rounded-sm font-bold tracking-wide uppercase text-sm transition-all disabled:opacity-50 ${compact ? 'bg-terracotta text-white hover:bg-terracotta/90' : 'btn-primary'}`}
        data-testid="newsletter-signup-submit-btn"
      >
        <Send size={14} /> {submitting ? 'Subscribing…' : 'Subscribe'}
      </button>
    </form>
  );
};

export default NewsletterSignup;
